import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import LotusLogo from "@/components/LotusLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-forest-800/15 bg-white/70 px-4 py-3 text-sm text-ink placeholder:text-sage/60 focus:outline-none focus:ring-2 focus:ring-gold-500/50 focus:border-gold-500 transition-shadow duration-300";

const AdminLogin = () => {
  usePageMeta("Admin Login | Holistic Healing Psychotherapy", "Secure admin access.");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const d = data.detail;
        throw new Error(typeof d === "string" ? d : "Login failed. Please try again.");
      }
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-forest-950 flex items-center justify-center px-5 py-20" data-testid="admin-login-page">
      <div className="w-full max-w-md rounded-2xl border border-gold-500/25 bg-forest-900 p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-8">
          <LotusLogo size={44} testId="admin-login-logo" />
          <div className="leading-tight">
            <p className="font-serif text-lg text-cream-50">Practice Admin</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-cream-50/50">Secure Access</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-cream-50/70 mb-2" htmlFor="al-email">Email</label>
            <input id="al-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@example.com" data-testid="admin-email-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-cream-50/70 mb-2" htmlFor="al-password">Password</label>
            <input id="al-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="admin-password-input" />
          </div>
          {error && <p className="text-sm text-red-300" role="alert" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-cream-50 text-forest-900 font-semibold text-sm px-7 py-3.5 transition-colors duration-300 hover:bg-white disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
